import React from 'react';

const actionTypes = {
  AUTOCOMPLETE_FOCUSED: 'FOCUSED',
  AUTOCOMPLETE_BLUR: 'CLICKED_OUTSIDE',
  AUTOCOMPLETE_INPUT_CHANGE: 'INPUT_CHANGE',
  AUTOCOMPLETE_SELECT_VALUE: 'SELECT_VALUE',
  AUTOCOMPLETE_INPUT_KEYWORD: 'SELECT_VALUE',
  AUTOCOMPLETE_CLEAR_VALUE: 'CLEAR_VALUE',
  AUTOCOMPLETE_OPEN: 'OPEN',
  AUTOCOMPLETE_CLOSE: 'CLOSE',
  AUTOCOMPLETE_OPTIONS_READY: 'OPTIONS_READY',
  AUTOCOMPLETE_FULFILL_INITIAL_DATA: 'INITIAL_DATA',
  AUTOCOMPLETE_SET_INITIAL_VALUE: 'SET_INITIAL_VALUE',
};

function autoCompleteReducer(state, { type, payload }) {
  switch (type) {
    case actionTypes.AUTOCOMPLETE_OPEN:
    case actionTypes.AUTOCOMPLETE_FOCUSED: {
      return { ...state, autocompleteState: 'OPEN', loading: true, options: [] }; // options: [], loading: true
    }

    case actionTypes.AUTOCOMPLETE_BLUR:
    case actionTypes.AUTOCOMPLETE_CLOSE: {
      return { ...state, autocompleteState: 'CLOSE', loading: false, options: [] };
    }
    case actionTypes.AUTOCOMPLETE_OPTIONS_READY: {
      return { ...state, autocompleteState: 'OPTIONS_FULFILLED', options: payload.options, loading: false };
    }
    case actionTypes.AUTOCOMPLETE_FULFILL_INITIAL_DATA: {
      return { ...state, autocompleteState: 'CLOSE', options: payload.options, loading: false };
    }
    case actionTypes.AUTOCOMPLETE_INPUT_CHANGE: {
      return { ...state, autocompleteState: 'FETCHING_OPTIONS', loading: true, inputValue: payload.inputValue };
    }
    case actionTypes.AUTOCOMPLETE_SELECT_VALUE: {
      return {
        ...state,
        autocompleteState: 'CLOSE',
        options: [],
        loading: false,
        value: payload.value,
        inputValue: payload.value?.name,
      };
    }
    case actionTypes.AUTOCOMPLETE_SET_INITIAL_VALUE: {
      return {
        ...state,
        autocompleteState: 'CLOSE',
        options: [],
        loading: false,
        value: payload.value,
        inputValue: payload.value?.name,
      };
    }
    case actionTypes.AUTOCOMPLETE_CLEAR_VALUE: {
      return {
        ...state,
        autocompleteState: 'CLOSE',
        options: [],
        loading: false,
        value: null,
        inputValue: '',
      };
    }
    default: {
      throw new Error(`Unsupported type: ${type}`);
    }
  }
}

/**
 * useFrontbaseAutocomplete Hook manages all potential states
 * here required for rendering the component.
 * SUPPORTED ONLY : Single Select
 *
 * @param id
 * @param name
 * @param value
 * @param multiple
 * @param required
 * @param loading
 * @param disableClearable
 * @param isEditable
 * @param options
 * @param noOptionsText
 * @param onAutocompleteOpen
 * @param onAutocompleteClose
 * @param onAutocompleteInputChange
 * @param onAutocompleteChange
 * @param onSearch
 * @param restOfThePropsAndHandlers
 * @returns {{noOptionsText: string, onChange: onChange, multiple: boolean, onInputChange: onInputChange, loading: boolean | "eager" | "lazy", required: boolean, disableClearable: boolean, onClose: onClose, onOpen: onOpen, "data-is-editable": boolean, "data-autocomplete-state": (string), name: *, options: (*[]|*), id: *, "data-testid": string, "data-loading-state": boolean | "eager" | "lazy", open: boolean}}
 */
export function useFrontbaseAutocomplete({
  id,
  name,
  value = null, // Should be array for multiple select
  inputValue = '',
  multiple = false,
  required = false,
  loading = false,
  disableClearable = false,
  isEditable = true,
  options,
  noOptionsText = 'No options',
  onOpen: onAutocompleteOpen,
  onClose: onAutocompleteClose,
  onFocus: onAutocompleteFocus,
  onBlur: onAutocompleteBlur,
  onInputChange: onAutocompleteInputChange,
  onChange: onAutocompleteChange,
  onSearch,
  ...restOfThePropsAndHandlers
}) {
  // Initialises loading options for reference
  const [state, dispatch] = React.useReducer(autoCompleteReducer, { loading, options, value, inputValue });

  // This state to get a reference and useful when
  // Component go through the cycle  -> OPEN -> CLOSE ->OPTIONS_FULFILLED by fetch request
  // Above lifecycle is pretty common during slow 3g load and potentially leaves the options drawer open
  const stateRef = React.useRef(state);
  React.useEffect(() => {
    stateRef.current = state;
  });

  // set initial data
  React.useEffect(() => {
    if (options && options.length > 0) {
      setInitialData({
        options,
      });
    }
  }, [options]);

  // set initial data
  React.useEffect(() => {
    setInitialValues(value);
  }, [value]);

  /**
   * Close event sames as Material UI with dispatch custom actions
   * @param event
   * @param reason
   */
  const close = (event, reason) => {
    const newPayload = { options: [] };
    dispatch({ type: actionTypes.AUTOCOMPLETE_CLOSE, payload: { ...newPayload } });
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteClose && onAutocompleteClose(event, reason);
  };

  /**
   * Clear event  with dispatch custom actions
   * @param event
   * @param reason
   */
  const clearValue = (event, reason) => {
    dispatch({ type: actionTypes.AUTOCOMPLETE_CLEAR_VALUE, payload: { options: [] } });
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteClose && onAutocompleteClose(event, reason);
  };

  /**
   * SelectValue  with dispatch custom actions
   * @param event
   * @param selectedValue
   * @param reason
   */
  const selectValue = (event, selectedValue) => {
    dispatch({
      type: actionTypes.AUTOCOMPLETE_SELECT_VALUE,
      payload: {
        value: selectedValue,
      },
    });
  };

  const setInitialValues = selectedValue => {
    dispatch({
      type: actionTypes.AUTOCOMPLETE_SET_INITIAL_VALUE,
      payload: {
        value: selectedValue,
      },
    });
  };

  /**
   * onOpen event same as Material UI with dispatch
   * @param event
   */
  const onOpen = event => {
    const newPayload = { options: state.options };
    dispatch({ type: actionTypes.AUTOCOMPLETE_OPEN, payload: { ...newPayload } });
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteOpen && onAutocompleteOpen(event, onOptionsReady);
  };

  /**
   * OnBlur event same as Material UI with dispatch
   *
   * Note: select-option is not implemented here
   * @param event
   * @param reason
   */
  const onClose = (event, reason) => {
    if (reason !== 'toggleInput') {
      close(event, reason);
    }
  };

  const onFocus = event => {
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteFocus && onAutocompleteFocus(event, onOptionsReady);
  };
  const onBlur = event => {
    close(null, 'blur');
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteBlur && onAutocompleteBlur(event);
  };

  /**
   * onChange event same as Material UI with dispatch
   *
   * @param event
   * @param reason
   */
  const onChange = (event, selectedValue, reason) => {
    if (reason === 'blur') {
      close(event, reason);
    }
    if (reason === 'clear') {
      clearValue(event, reason);
    }
    if (reason === 'select-option') {
      selectValue(event, selectedValue, reason);
    }
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteChange && onAutocompleteChange(event, selectedValue, reason);
  };

  /**
   * onOptionsReady sets the options only if previous state is not closed
   * usecase: In slow 3g, user can click multiple filters and goes through
   * OPEN -> CLOSE -> OPTIONS_FULFILLED
   * handle this case where the options shouldn't be set when its already closed
   *
   * @param payload
   */
  const onOptionsReady = payload => {
    if (stateRef.current.autocompleteState !== 'CLOSE') {
      dispatch({ type: actionTypes.AUTOCOMPLETE_OPTIONS_READY, payload: { ...payload } });
    }
  };
  /**
   * Set Initial data
   * @param payload
   */
  const setInitialData = payload => {
    dispatch({ type: actionTypes.AUTOCOMPLETE_FULFILL_INITIAL_DATA, payload: { ...payload } });
  };

  /**
   * onInputChange sames as Material UI with dispatch
   * Enhanced with onSearch functionality
   * @param event
   * @param value
   * @param reason
   */
  const onInputChange = (event, typedValue, reason) => {
    if (reason === 'input') {
      dispatch({ type: actionTypes.AUTOCOMPLETE_INPUT_CHANGE, payload: { inputValue: typedValue } });
      // eslint-disable-next-line no-unused-expressions
      onSearch && onSearch(typedValue, onOptionsReady);
    }
    // eslint-disable-next-line no-unused-expressions
    onAutocompleteInputChange && onAutocompleteInputChange(event, typedValue, reason);
  };

  return {
    id,
    name,
    multiple,
    required,
    loading: state.loading,
    disableClearable,
    options: typeof state.options === 'undefined' ? [] : state.options,
    noOptionsText,
    onOpen,
    onClose,
    onInputChange,
    onChange,
    onBlur,
    onFocus,
    open:
      state.autocompleteState === 'OPTIONS_FULFILLED' ||
      state.autocompleteState === 'FETCHING_OPTIONS' ||
      state.autocompleteState === 'OPEN',
    'data-testid': `fb-autocomplete-${id}`,
    'data-is-editable': isEditable,
    'data-autocomplete-state': state.autocompleteState,
    'data-loading-state': state.loading,
    value: state.value ? state.value : null,
    inputValue: state.inputValue ? state.inputValue : '',
    ...restOfThePropsAndHandlers,
  };
}
